



















































































































import { Component, Vue, Watch, Emit, Prop } from "vue-property-decorator";
import PatientSelector from "@/components/inputs/PatientSelector.vue";
import { PatientModel } from "@/models/patient";
import { auth as authStore, auth, patient as store } from "@/store/modules";
import { inject } from "inversify-props";
import { StockItemsTableExpandedCard } from '@/components/Patient/StockItemsTableExpandedCard.vue';
import { IEventService, IPatientService, StockFilter, IStockService, StockOrderBy, OrderByMode } from "@/interfaces/Services";
import MassActionPerformModal, { MassActionItem } from '@/components/MassActionPerformModal.vue';
import { DateInput } from '@/components/inputs';
import DependencyIdentifiers from '@/ioc/DependencyIdentifiers';
import { StockItemModel, StockItemStatsModel, StockItemStatus } from '@/models/stock';
import { DrugModel } from '../../models/drug';

enum StockGroupActionable { Returnable = 'returnable' }

@Component({
  components: {
    MassActionPerformModal,
    StockItemsTableExpandedCard,
  }
})
export class StockItemsTableCard extends Vue {
  @inject(DependencyIdentifiers.StockService)
  private stockService!: IStockService;
  @inject(DependencyIdentifiers.PatientService)
  private patientService!: IPatientService;

  @Prop({required: true, default: {}})
  private filter!: StockFilter;

  @Prop({required: false, default: false})
  private flat?: boolean;

  private isLoading = false;
  private componentKey = 0;
  private items: Array<StockItemStatsModel> = [];
  private total = 0;
  private selected: StockItemStatsModel[] = [];
  private expandedItem: StockItemStatsModel[] = [];
  private patients: Array<PatientModel> = [];
  private isSingleExpand = false;

  private markAsReturnedModal = {
    isVisible: false,
    inProgress: false,
    queue: [] as MassActionItem[]
  };

  private searchQuery: string = '';
  private searchQueryDebounceTimer: NodeJS.Timeout | null = null;

  private get headers() {
    return [
      {
        text: this.$t('stock.columns.drug'),
        align: "left",
        sortable: false,
        value: "drug"
      },
      {
        text: this.$t('stock.columns.countAvailable'),
        align: "left",
        sortable: false,
        value: "available"
      },
      {
        text: this.$t('stock.columns.countPossiblyUsed'),
        align: "left",
        sortable: false,
        value: "possiblyUsed"
      },
      {
        text: this.$t('stock.columns.countReturned'),
        align: "left",
        sortable: false,
        value: "returned",
      },
      {
        text: "",
        value: "action",
        sortable: false,
        align: "right"
      },
      { text: "", value: "data-table-expand" }
    ];
  }

  private options = {
    page: 1,
    itemsPerPage: 10,
    sortBy: ["status"],
    sortDesc: [true],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false
  };

  async mounted() {
    await this.loadPatients();
    await this.refresh();
  }

  private isSelectedActionable(
    items: StockItemStatsModel[],
    check: StockGroupActionable,
    matchAll: boolean = true,
  ): boolean {
    let matchCount: number = 0;
    items.forEach((item) => {
      switch (check) {
        case StockGroupActionable.Returnable:
          if (item.counters.possibly_used > 0 || item.counters.available > 0) {
            matchCount++;
          }
          break;
      }
    });

    return matchAll ? matchCount == items.length : matchCount > 0;
  }

  private async loadPatients() {
    try {
      const page = 1,
        itemsPerPage = 100;
      const [patients, {}] = await this.patientService.getMany(
        page,
        itemsPerPage
      );
      this.patients = [...patients];
    } finally {
      this.isLoading = false;
    }
  }

  private async refresh() {
    this.isLoading = true;
    this.expandedItem = [];
    try {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;

      const [items, total] = await this.stockService.stats(
        page,
        itemsPerPage,
        this.filter,
      );

      this.total = total;
      this.items = [...items];
    } finally {
      this.isLoading = false;
    }
  }

  private async reset() {
    this.options.page = 1;
    // IMPORTANT: Since there's no pagination we don't need to refresh as of now.
    // await this.refresh();
  }

  private resolvePatientById(patientId: string) {
    let patient = Object.values(store.items).find(
      ({ $patientId }) => $patientId === patientId
    );
    if (patient) {
      return patient.name;
    }
    patient = this.patients.find(({ $id }) => $id === patientId);
    if (patient) {
      return patient.name;
    }
    return null;
  }

  async autoselectReturn(items: StockItemStatsModel[], type: StockItemStatus, amount: number = 1) {
    this.isLoading = true;

    const queue: Promise<[StockItemModel[], number]>[] = [];
    items.forEach(stat => {
      queue.push(this.stockService.getMany(
        1,
        amount,
        {
          patientId: this.filter.patientId,
          drugId: stat.$drug!.$id,
          statuses: [type]
        },
        new Map([[StockOrderBy.$updatedAt, OrderByMode.Asc]]),
      ));
    });

    const result = await Promise.all(queue)
      .then(resultRaw => resultRaw.filter(result => !!result))
      .then(resultRaw => {
        if (!resultRaw || resultRaw.length < 1) { return; }
        return resultRaw
          .reduce((carry, items) => {
            return carry = carry.concat(items[0])
          }, [] as StockItemModel[]);
      });

    if (result && result.length > 0) {
      this.markAsReturned(result);
    } else {
      alert(this.$t("stock.errorMissingReturnableItem"));
      this.isLoading = false;
    }
  }

  private markAsReturned(items: StockItemModel[]) {
    this.isLoading = true;
    this.markAsReturnedModal.queue = [...items].map<MassActionItem>((item: StockItemModel) => {
      return {
        model: item,
        identifier: item.$createdAt!.toString(),
        getActionResult: async () => await this.stockService.return(item.$id!.toString())
      };
    });
    this.markAsReturnedModal.inProgress = true;
    this.markAsReturnedModal.isVisible = true;
    this.refresh();
  }

  @Watch("options", { deep: true })
  private async onOptionsChange(val: any, oldVal: any) {
    await this.refresh();
  }

  @Watch("searchQuery")
  private async onSearchQueryChange(val: string, oldVal: string) {
    if (val == null || val == oldVal) {
      return;
    }

    if (this.searchQueryDebounceTimer != null) {
      clearTimeout(this.searchQueryDebounceTimer);
    }
    this.searchQueryDebounceTimer = setTimeout(
      async () => this.searchQueryUpdated(),
      200
    );
  }

  @Emit("search-query:updated")
  private searchQueryUpdated() {
    return this.searchQuery;
  }

  private onStateUpdateComplete() {
    this.markAsReturnedModal.inProgress = false;
  }

  private onStateUpdateModalClose() {
    this.markAsReturnedModal.queue = [];
    this.markAsReturnedModal.inProgress = false;
    this.markAsReturnedModal.isVisible = false;
    this.isLoading = false;
    this.refresh();
  }

  isFlat() {
    return this.flat;
  }
}

export default StockItemsTableCard;
